import { Discount, Region } from "@medusajs/medusa"
import { PricedProduct } from "@medusajs/medusa/dist/types/pricing"
import { useQuery } from "@tanstack/react-query"
import { useSearchParams } from "next/navigation"
import { useMemo } from "react"

import { getProductsDiscounts, retrievePricedProductById } from "@lib/data"
import { getRegion } from "app/actions"

const useProductData = (productId: string | null) => {
  const {
    data: productData,
    isLoading: productLoading,
    isFetching,
  } = useQuery({
    queryKey: ["product-data", productId],
    queryFn: async () => {
      if (!productId) return null

      const fetchedRegion = await getRegion("mx")

      const pricedProduct = await retrievePricedProductById({
        id: productId,
        regionId: fetchedRegion?.id ?? "",
      })

      const discountRes = await getProductsDiscounts([productId])
      const discounts = discountRes?.[0]?.discounts || []

      return {
        product: pricedProduct,
        region: fetchedRegion,
        discounts,
      }
    },
    enabled: !!productId,
    refetchOnWindowFocus: false,
  })

  const product: PricedProduct | null = productData?.product || null
  const discounts: Discount[] = productData?.discounts || []
  const region: Region | null = productData?.region || null

  const searchParams = useSearchParams()
  const variantId = searchParams.get("variantId")

  const selectedVariant = useMemo(() => {
    if (!product || product.variants.length === 0) {
      return null
    }
    return variantId ? product.variants.find((v) => v.id === variantId) || null : null
  }, [product, variantId])

  return {
    product,
    discounts,
    region,
    variantId,
    selectedVariant,
    productLoading,
    isFetching,
  }
}

export default useProductData
